import { useQuery } from "@apollo/client";
import { Alert, Button, Stack } from "@mui/material";
import { DashboardLayout } from "components/Dashboard/DashboardLayout";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { StatCard, StatCardRowSkeleton } from "components/StatCard";
import { WasteComplianceFindingsQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { useMemo, useRef, useState } from "react";

import { WASTE_COMPLIANCE_FINDINGS_QUERY } from "./api";
import {
  computeKpis,
  FeedSortKey,
  kindFilterOptions,
  kindsForLabel,
  sortFeed,
  toFeedItems,
  type ComplianceKpis,
  type KindFilterOption,
} from "./complianceFeed";
import { ComplianceFeedControls } from "./ComplianceFeedControls";
import { ComplianceFeedRow } from "./ComplianceFeedRow";

/**
 * How much of each source to pull into the feed. The dashboard is a triage
 * surface, not an exhaustive register — both sources can run to thousands of open
 * findings, and the KPI row reports the true totals separately so a capped list
 * never reads as "this is everything".
 */
const CONTAINER_PAGE_SIZE = 50;
const MANIFEST_PAGE_SIZE = 200;

/**
 * The type chips, held steady across filter changes.
 *
 * `countsByKind` covers the rule sets a request actually read, and a kind-filtered
 * request reads fewer of them, so the widest set seen so far wins: dropping to a
 * filtered response's subset would remove the chips the user needs to get back.
 * The dashboard opens unfiltered, so the widest set is the first to arrive.
 *
 * A ref rather than state because this is a high-water mark, not a render input —
 * a new best is already accompanied by the render that delivered it.
 */
function useKindFilterOptions(data: WasteComplianceFindingsQuery | undefined) {
  const widest = useRef<readonly KindFilterOption[]>([]);
  const container = data?.tenant?.containerComplianceFindings.countsByKind;
  const manifest = data?.tenant?.manifestComplianceFindings.countsByKind;

  if (container && manifest) {
    const next = kindFilterOptions({ container, manifest });
    if (next.length >= widest.current.length) {
      widest.current = next;
    }
  }

  return widest.current;
}

/** The five headline counts, describing the whole feed rather than the filtered view. */
function ComplianceKpiRow({ kpis }: { kpis: ComplianceKpis }) {
  return (
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
      <StatCard value={String(kpis.openFindings)} caption="Open findings" />
      <StatCard
        value={String(kpis.overdue)}
        caption="Overdue"
        valueColor={kpis.overdue > 0 ? "error.main" : "text.primary"}
      />
      <StatCard
        value={String(kpis.dueWithinSevenDays)}
        caption="Due in 7 days or less"
        valueColor={kpis.dueWithinSevenDays > 0 ? "warning.main" : "text.primary"}
      />
      {/* Provenance, not scope — reads as "these came from container
          tracking", so a profile finding counting here is not a surprise. */}
      <StatCard value={String(kpis.containerTrackingFindings)} caption="From container tracking" />
      <StatCard
        value={String(kpis.facilitiesAtRisk)}
        caption={`${pluralize("Facility", kpis.facilitiesAtRisk)} at risk`}
      />
    </Stack>
  );
}

/**
 * The waste compliance dashboard: one urgency-sorted feed of every open
 * finding, container-tracking and manifest-derived alike, over five headline
 * counts. Laid out as dashboard tiles so it reads like the other waste
 * dashboards — white cards on the gray canvas.
 */
export function WasteCompliance() {
  useBreadcrumb({ label: "Waste Compliance" });
  const { tenantId } = useTenant();
  // null = All types.
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<FeedSortKey>("urgency");
  const [page, setPage] = useState(0);

  // Narrowing happens server-side, so a type filter reaches findings that no page
  // of the unfiltered feed would have contained.
  const selection = kindsForLabel(kindFilter);

  const { data, loading, error, fetchMore } = useQuery(WASTE_COMPLIANCE_FINDINGS_QUERY, {
    // `skip` guards the empty id; the coalesce only satisfies the variable type.
    variables: {
      tenantId: tenantId ?? "",
      containerKinds: selection.containerKinds,
      manifestKinds: selection.manifestKinds,
      page: 0,
      containerPageSize: CONTAINER_PAGE_SIZE,
      manifestPageSize: MANIFEST_PAGE_SIZE,
    },
    skip: !tenantId,
    // Findings are derived at read, so a revisit should reflect anything fixed
    // since the last look rather than replaying a cached feed.
    fetchPolicy: "cache-and-network",
  });

  // `now` is stamped alongside the feed it describes, so the KPI counts and the
  // row pills are always read against the same instant.
  const { items: allItems, now } = useMemo(() => {
    const stampedAt = new Date();
    const tenant = data?.tenant;
    if (!tenant) return { items: [], now: stampedAt };
    // Link paths come off the resolved tenant rather than the hook, so the ids in
    // the feed and the ids the rows link to are always the same tenant.
    return {
      items: toFeedItems(
        tenant.id,
        tenant.containerComplianceFindings.items,
        tenant.manifestComplianceFindings.items
      ),
      now: stampedAt,
    };
  }, [data]);

  const containerTotal = data?.tenant?.containerComplianceFindings.count ?? 0;
  const manifestTotal = data?.tenant?.manifestComplianceFindings.count ?? 0;
  const totalOpen = containerTotal + manifestTotal;
  const shown = allItems.length;
  const hasMore = shown < totalOpen;

  // Every loaded row is re-sorted together, so growing the feed cannot leave it
  // in a per-source order — which is why this is "load more" and not page N.
  const visibleItems = useMemo(() => sortFeed(allItems, sortKey), [allItems, sortKey]);

  const filterOptions = useKindFilterOptions(data);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    void fetchMore({ variables: { page: next } });
  };

  // Off the filtered totals, not the loaded rows: the headline should describe
  // what the current filter matches rather than how far you have scrolled.
  const kpis = useMemo(
    () => computeKpis(allItems, now, { containerTotal, manifestTotal }),
    [allItems, now, containerTotal, manifestTotal]
  );

  return (
    <DashboardLayout>
      <DashboardTile size={{ xs: 12 }}>
        <DashboardTile.ContentArea>
          {loading && !data ? <StatCardRowSkeleton count={5} /> : <ComplianceKpiRow kpis={kpis} />}
        </DashboardTile.ContentArea>
      </DashboardTile>

      <DashboardTile size={{ xs: 12 }}>
        {/* No count in the title — the KPI tile above already reports it. */}
        <DashboardTile.Header title="Open findings" />
        <ComplianceFeedControls
          options={filterOptions}
          totalCount={totalOpen}
          selectedKind={kindFilter}
          onSelectKind={(kind) => {
            // Back to the first page: the new filter has its own ordering, and
            // keeping the old offset would open partway down it.
            setPage(0);
            setKindFilter(kind);
          }}
          sortKey={sortKey}
          onSortChange={setSortKey}
        />
        <DashboardTile.ContentArea>
          <Stack spacing={1.5}>
            {error && <Alert severity="error">Could not load compliance findings.</Alert>}

            {/* `!error`: a failed query with no cache is also empty, and the green
                alert would otherwise sit under the red one. */}
            {!loading && !error && visibleItems.length === 0 && (
              <Alert severity="success">
                {kindFilter === null ? "No open compliance findings." : "No findings of this type."}
              </Alert>
            )}

            {visibleItems.map((item) => (
              <ComplianceFeedRow key={item.id} item={item} now={now} />
            ))}

            {hasMore && (
              <Stack alignItems="center" sx={{ pt: 1 }}>
                <Button onClick={loadMore} disabled={loading}>
                  {loading ? "Loading…" : `Load more (${shown} of ${totalOpen})`}
                </Button>
              </Stack>
            )}
          </Stack>
        </DashboardTile.ContentArea>
      </DashboardTile>
    </DashboardLayout>
  );
}
